import React, { useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Box, Container, Typography, Grid } from '@mui/material';
import { KBHeader } from '../components/KBHeader';
import { KBSidebar } from '../components/KBSidebar';
import { ArticleCard } from '../components/ArticleCard';
import { useKBTheme } from '../hooks/useKBTheme';
import { articles } from '../data/articles';

const categoryMap: Record<string, string> = {
  'smart-rules': 'Smart Policies',
  'smart-policies': 'Smart Policies',
  'getting-started': 'Getting Started',
  'admin': 'Admin Console',
  'chat': 'Chat Interface',
  'tickets': 'Tickets App',
  'teams-roles': 'Teams & Roles',
  'best-practices': 'Best Practices',
};

export const KnowledgeBasePage: React.FC = () => {
  const { mode } = useKBTheme();
  const { category } = useParams<{ category?: string }>();
  const location = useLocation();
  
  // Extract category from URL path - useParams first, then fallback to path parsing
  const categoryParam = category || (location.pathname.match(/\/category\/([^/]+)/)?.[1] || null);
  const selectedCategory = categoryParam ? (categoryMap[categoryParam] || categoryParam) : null;
  const filteredArticles = selectedCategory 
    ? articles.filter((a) => a.category === selectedCategory)
    : articles;
  
  // Scroll to top when category changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [categoryParam]);
  
  // Debug logging (remove in production)
  useEffect(() => {
    if (categoryParam) {
      console.log('Category param:', categoryParam, 'Selected category:', selectedCategory, 'Articles:', filteredArticles.length);
    }
  }, [categoryParam, selectedCategory, filteredArticles.length]);

  const getBackgroundGradient = () => {
    if (mode === 'dark') {
      return 'linear-gradient(180deg, #0f172a 0%, #151b26 25%, #1a2332 50%, #1c2838 75%, #1e293b 100%)';
    }
    return 'linear-gradient(180deg, #FAFBFC 0%, #FAFAFC 25%, #F9FAFB 50%, #F9FAFC 75%, #F8FAFC 100%)';
  };

  const featuredArticles = selectedCategory ? filteredArticles.slice(0, 6) : articles.slice(0, 6);
  const categories = Array.from(new Set(articles.map((a) => a.category)));

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: getBackgroundGradient(),
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: mode === 'dark'
            ? `radial-gradient(circle at 20% 80%, rgba(99,102,241,0.08) 0%, transparent 50%),
               radial-gradient(circle at 80% 20%, rgba(236,72,153,0.08) 0%, transparent 50%)`
            : `radial-gradient(circle at 20% 80%, rgba(99, 102, 241, 0.05) 0%, transparent 50%),
               radial-gradient(circle at 80% 20%, rgba(236, 72, 153, 0.05) 0%, transparent 50%)`,
          pointerEvents: 'none',
        },
      }}
    >
      <KBHeader onSearch={() => {}} />
      <Box sx={{ display: 'flex', position: 'relative', zIndex: 1 }}>
        <KBSidebar />
        <Box sx={{ flex: 1, p: 4 }}>
          <Container maxWidth="xl" sx={{ px: 0 }}>
            {selectedCategory ? (
              <>
                <Box sx={{ mb: 4 }}>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 800,
                      mb: 2,
                      color: mode === 'dark' ? '#fff' : '#0f172a',
                      fontSize: { xs: '2rem', md: '3rem' },
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Arial, sans-serif',
                    }}
                  >
                    {selectedCategory}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#64748b',
                      fontWeight: 400,
                      lineHeight: 1.6,
                    }}
                  >
                    {filteredArticles.length} {filteredArticles.length === 1 ? 'article' : 'articles'} in this category
                  </Typography>
                </Box>
                <Grid container spacing={3}>
                  {filteredArticles.map((article) => (
                    <Grid item xs={12} md={6} lg={4} key={article.id}>
                      <ArticleCard
                        id={article.id}
                        title={article.title}
                        description={article.description}
                        category={article.category}
                        tags={article.tags}
                        readTime={article.readTime}
                      />
                    </Grid>
                  ))}
                </Grid>
              </>
            ) : (
              <>
                <Box sx={{ mb: 6 }}>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 800,
                      mb: 2,
                      color: mode === 'dark' ? '#fff' : '#0f172a',
                      fontSize: { xs: '2rem', md: '3rem' },
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Arial, sans-serif',
                    }}
                  >
                    Welcome to CoreSupport Knowledge Base
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      color: mode === 'dark' ? 'rgba(255,255,255,0.8)' : '#64748b',
                      fontWeight: 400,
                      lineHeight: 1.6,
                    }}
                  >
                    Learn how to deploy your expert AI agent, automate routine tasks, and supercharge your team.
                  </Typography>
                </Box>

                <Box sx={{ mb: 6 }}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      mb: 3,
                      color: mode === 'dark' ? '#fff' : '#0f172a',
                    }}
                  >
                    Featured Articles
                  </Typography>
                  <Grid container spacing={3}>
                    {featuredArticles.map((article) => (
                      <Grid item xs={12} md={6} lg={4} key={article.id}>
                        <ArticleCard
                          id={article.id}
                          title={article.title}
                          description={article.description}
                          category={article.category}
                          tags={article.tags}
                          readTime={article.readTime}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>

                {categories.map((cat) => {
                  const categoryArticles = articles.filter((a) => a.category === cat);
                  return (
                    <Box key={cat} sx={{ mb: 6 }}>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          mb: 3,
                          color: mode === 'dark' ? '#fff' : '#0f172a',
                        }}
                      >
                        {cat}
                      </Typography>
                      <Grid container spacing={3}>
                        {categoryArticles.slice(0, 3).map((article) => (
                          <Grid item xs={12} md={6} lg={4} key={article.id}>
                            <ArticleCard
                              id={article.id}
                              title={article.title}
                              description={article.description}
                              category={article.category}
                              tags={article.tags}
                              readTime={article.readTime}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  );
                })}
              </>
            )}
          </Container>
        </Box>
      </Box>
    </Box>
  );
};

